import { Button, useToast } from '@hkdevs/design-system';
import { useState } from 'react';
import {
  useDownloadOriginal,
  useExportPayHistory,
  type UploadSummary,
} from '../../api/queries/uploads';
import { PageLayout } from '../../shell/Page';
import { downloadBlob } from '../payHistory/csv';
import { PayHistoryUploadsView, type PayDataHistorySearch } from './PayHistoryUploadsView';
import { UploadFilesDrawer } from './UploadFilesDrawer';
import { DeleteUploadModal, UploadErrorReportModal, type UploadRef } from './UploadModals';
import { withReference } from '../../api/withReference';

/**
 * Pay history uploads (pc-50i.2): the header's Export and Upload files over the uploads,
 * with the Upload files drawer, a rejected upload's error report and Delete upload.
 */
export function PayHistoryUploadsPage({
  search,
  onSearchChange,
}: {
  search: PayDataHistorySearch;
  onSearchChange: (next: PayDataHistorySearch) => void;
}) {
  const { toast } = useToast();
  // A new key each time the drawer opens, so it starts at step 1 with no files.
  const [drawerKey, setDrawerKey] = useState<number | null>(null);
  const [report, setReport] = useState<UploadRef | null>(null);
  const [deleting, setDeleting] = useState<UploadRef | null>(null);
  const exportFile = useExportPayHistory();
  const original = useDownloadOriginal();

  const openUpload = () => setDrawerKey(Date.now());
  const exportHistory = () =>
    exportFile.mutate(
      { from: search.from, to: search.to },
      {
        onSuccess: ({ blob, fileName }) => downloadBlob(blob, fileName),
        onError: (error) =>
          toast(
            withReference(
              {
                tone: 'danger',
                title: 'Pay history couldn’t be exported',
                description: 'Check your connection and try again.',
              },
              error,
            ),
          ),
      },
    );
  const download = (upload: UploadSummary) =>
    original.mutate(upload, {
      onSuccess: ({ blob, fileName }) => downloadBlob(blob, fileName),
      onError: (error) =>
        toast(
          withReference(
            {
              tone: 'danger',
              title: `${upload.fileName} couldn’t be downloaded`,
              description: 'Check your connection and try again.',
            },
            error,
          ),
        ),
    });

  return (
    <PageLayout
      page="payDataHistory"
      actions={
        <>
          <Button
            size="sm"
            variant="secondary"
            leadingIcon="download"
            onClick={exportHistory}
            loading={exportFile.isPending}
            loadingText="Exporting…"
          >
            Export
          </Button>
          <Button size="sm" leadingIcon="upload" onClick={openUpload}>
            Upload files
          </Button>
        </>
      }
    >
      <PayHistoryUploadsView
        search={search}
        onSearchChange={onSearchChange}
        onUpload={openUpload}
        onDownload={download}
        onOpenErrorReport={setReport}
        onDelete={setDeleting}
        announce={drawerKey === null}
      />
      {drawerKey !== null && (
        <UploadFilesDrawer
          key={drawerKey}
          open
          onClose={() => setDrawerKey(null)}
          onOpenErrorReport={setReport}
        />
      )}
      {report && <UploadErrorReportModal target={report} onClose={() => setReport(null)} />}
      {deleting && <DeleteUploadModal target={deleting} onClose={() => setDeleting(null)} />}
    </PageLayout>
  );
}
